import IconChevronDown from '@remote-com/norma/icons/chevron-down.svg';
import {
  fluidProp,
  whenProp,
  unlessProp,
  whenPropOrSelector,
  focusVisible,
} from 'remote-com-shared/style/helpers';
import { breakpointFrom } from 'remote-com-shared/style/mediaQueries';
import { sharedTransition } from 'remote-com-shared/style/motion';
import styled, { css } from 'styled-components';

import {
  fromBreakpointReference,
  sharedShadowDown,
  sharedShadowUp,
  untilBreakpointReference,
} from './shared';

export const StyledMenuEntry = styled.li`
  position: relative;

  ${fromBreakpointReference(css`
    & + & {
      ${fluidProp('margin-left', '0px', '30px')}
    }
  `)}
`;

const BaseMenuItem = styled.button`
  // Button reset
  color: inherit;
  font-weight: inherit;
  font-size: inherit;
  border: none;
  width: 100%;

  background-color: transparent;
  position: relative;
  display: flex;
  align-items: center;
  padding: var(--entryYSpace) var(--entryXSpace);
  cursor: pointer;
`;

export const SubMenuIndicator = styled(IconChevronDown)`
  display: block;
  position: relative;
  flex-shrink: 0;
  top: 2px;
  width: var(--arrowDecoratorWidth);
  height: var(--arrowDecoratorWidth);
  margin-left: var(--arrowDecoratorMargin);

  ${untilBreakpointReference(css`
    fill: var(--colors-lynch);

    ${whenProp(
      '$isOpen',
      css`
        fill: currentColor;
        transform: rotate(180deg);
      `
    )}
  `)}

  ${fromBreakpointReference(css`
    opacity: 1;
    transition: opacity var(--leaveDuration) ease-in-out;
    transition-timing-function: var(--leaveTiming);

    ${StyledMenuEntry}:hover &,
    ${StyledMenuEntry}:focus-within & {
      opacity: 0;
    }
  `)}
`;

export const StyledMenuItem = styled(BaseMenuItem)`
  justify-content: space-between;

  ${focusVisible(css``)}

  ${untilBreakpointReference(css`
    ${sharedTransition('color, background-color')}

    ${whenPropOrSelector(
      '$isOpen',
      '&:hover, &:focus, &:focus-within',
      css`
        color: var(--colors-irisBlue);
        background-color: var(--colors-selago);

        &::before {
          opacity: 0;
        }
      `
    )}

    &::before {
      ${sharedTransition('background-color')}

      content: '';
      position: absolute;
      left: var(--panelXSpace);
      right: var(--panelXSpace);
      bottom: 0;
      height: 1px;
      background-color: var(--colors-mischka);
    }
  `)}

  ${fromBreakpointReference(css`
    ${sharedTransition('color')}

    text-align: center;

    &:hover,
    &:focus {
      color: var(--menuInteractionHighlightColor);
    }

    // Underline effect on hover
    ${unlessProp(
      '$hasSubMenu',
      css`
        &::before {
          content: '';
          display: block;
          position: absolute;
          bottom: calc(var(--entryYSpace) - 10px);
          left: 0;
          height: 2px;
          width: 100%;
          transform: scaleX(0);
          transform-origin: right top;
          transition: color 0.3s, transform 0.4s ease-out;
          background-color: var(--menuInteractionHighlightBorderColor);
        }

        ${whenPropOrSelector(
          '$isCurrent',
          '&:hover, &:focus',
          css`
            &::before {
              transform: scale(1);
              transform-origin: left top;
            }
          `
        )}
      `
    )}
  `)}
`;

export const SubMenuItem = styled(BaseMenuItem)`
  ${sharedTransition('color')}
  row-gap: 10px;

  ${focusVisible(css`
    background-color: ${({ theme }) => theme.colors.selago_60};
  `)}

  &:hover,
  &:focus {
    color: var(--colors-irisBlue);
  }

  ${untilBreakpointReference(css`
    color: var(--colors-bayoux);
    background-color: var(--colors-alabaster);
  `)}
`;

const MenuPanelStyle = css`
  display: none;
  color: var(--colors-darkBlue);

  ${untilBreakpointReference(css`
    ${whenProp(
      '$isOpen',
      css`
        display: block;
      `
    )}
  `)}

  ${fromBreakpointReference(css`
    --panelYSpace: 22px;
    --entryYSpace: 10px;
    --entryXSpace: var(--panelXSpace);
    --horizontalOffsetPercent: -48%;
    --horizontalOffset: translateX(
      calc(
        var(--horizontalOffsetPercent) - var(--arrowDecoratorWidth) + var(--arrowDecoratorMargin)
      )
    );
    --panelTipDecoratorHorizontalOffset: 50%;
    --perspective: perspective(600px);

    display: block;
    position: absolute;
    pointer-events: none;
    left: 50%;
    background-color: var(--colors-blank);
    padding-top: var(--panelYSpace);
    padding-bottom: var(--panelYSpace);
    border-radius: 10px;
    box-shadow: ${sharedShadowDown}, ${sharedShadowUp};
    transition: var(--leaveDuration);
    transition-timing-function: var(--leaveTiming);
    transition-property: transform, opacity;
    transform: var(--horizontalOffset) var(--perspective) rotateX(-20deg);
    transform-origin: center top;
    opacity: 0;

    ${({ $columns }) => {
      switch ($columns) {
        case '3-wide':
          return css`
            width: calc(320px * 3);
          `;
        case '2-wide':
          return css`
            width: calc(324px * 2);
          `;
        case '2':
          return css`
            width: calc(240px * 2);
          `;
        default:
          return css`
            width: 294px;
          `;
      }
    }}

    ${StyledMenuEntry}:hover > &,
    ${StyledMenuEntry}:focus-within > & {
      transition: var(--enterDuration);
      transition-timing-function: var(--enterTiming);
      transform: var(--horizontalOffset) var(--perspective) rotateX(0deg);
      opacity: 1;
      pointer-events: auto;
    }

    &::before {
      content: '';
      display: block;
      position: absolute;
      top: -8px;
      left: var(--panelTipDecoratorHorizontalOffset);
      transform: translateX(-50%);
      border-style: solid;
      border-color: transparent;
      border-width: 0 8px 8px 8px;
      border-bottom-color: var(--colors-blank);
    }

    // First menu item has corrected offset to remain in view
    ${StyledMenuEntry}:first-child > & {
      --horizontalOffsetPercent: -28%;
      --panelTipDecoratorHorizontalOffset: 29%;
    }

    // Last menu item has corrected offset to remain in view
    ${StyledMenuEntry}:last-child > & {
      --horizontalOffsetPercent: -60%;
      --panelTipDecoratorHorizontalOffset: 61%;
    }
  `)}
`;

export const MenuPanel = styled.div`
  ${MenuPanelStyle}
`;

export const SubMenuEntry = styled.li`
  & + & {
    margin-top: 2px;
  }
`;

export const SubMenuIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-right: 15px;

  svg {
    fill: var(--colors-irisBlue);
  }
`;

export const SubMenuText = styled.span``;

export const SubMenuDescription = styled.span`
  font-size: 0.6875em;
  font-weight: 300;
`;

export const SubMenuGroups = styled.div`
  ${breakpointFrom.md(css`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(234px, 1fr));
    gap: 10px;
  `)}
`;

export const SubMenuGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

export const SubMenu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  ${SubMenuGroups} + & {
    margin-top: var(--entryYSpace);
  }

  ${SubMenuGroup} > & {
    flex-grow: 1;
  }

  ${breakpointFrom.md(css`
    // Vertical line decorator
    ${SubMenuGroup} + ${SubMenuGroup} & {
        position: relative;

        &::before {
          content: '';
          display: block;
          width: 1px;
          background-color: var(--colors-darkBlue);
          opacity: 0.2;
          position: absolute;
          top: 0;
          left: -5px;
          bottom: 0;
        }
      }
    }
  `)};
`;

export const SubMenuGroupLabel = styled.div`
  ${({ theme }) => theme.typography.subHeadXS}
  text-transform: uppercase;
  color: var(--colors-lynch);
  padding: var(--entryYSpace) var(--panelXSpace);
`;

export const ClickableSubMenuGroupLabel = styled(SubMenuGroupLabel)`
  && {
    color: var(--colors-irisBlue);

    &:hover,
    &:focus {
      text-decoration: underline;
    }
  }
`;
